/**
 * CompaniesPage Component
 * 
 * Main page for company management
 * Handles CRUD operations and integration management
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyList } from '@/components/companies/CompanyList';
import { CompanyFormDialog } from '@/components/companies/CompanyFormDialog';
import { IntegrationConfigDialog } from '@/components/companies/IntegrationConfigDialog';
import { toast } from 'sonner';
import * as companyService from '@/services/companyService';
import { 
  getProductInstancesForCompany,
  createProductInstance,
  updateProductInstance,
  deleteProductInstance,
  syncProductInstance,
  mapProductInstanceDtoToProductInstance,
} from '@/services/integrationsApi';
import { mockCompanyService } from '@/data/mockCompanies';
import { getMockProductInstancesForCompany } from '@/data/mockProductInstances';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import type { CompanyListItem, CreateCompanyRequest, UpdateCompanyRequest, AddProductRequest, UpdateProductRequest } from '@/types';
import type { Company, ProductInstance } from '@/features/multi-company/types/company.types';

export function CompaniesPage() {
  const navigate = useNavigate();
  const { isDemo, isLive } = useScreenDataMode('integrations');
  const { refreshCompanies: refreshHeaderCompanies } = useCompany();
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [companyProductInstances, setCompanyProductInstances] = useState<Record<string, ProductInstance[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isIntegrationDialogOpen, setIsIntegrationDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // Load companies on mount and when mode changes
  useEffect(() => {
    loadCompanies();
  }, [isDemo]);

    const loadCompanies = async () => {
      setIsLoading(true);
      try {
        if (isDemo) {
          const data = await mockCompanyService.getCompanies();
          setCompanies(data);
          setCompanyProductInstances({});
        } else {
          const data = await companyService.getCompanies();
          setCompanies(data);
        
          // Load product instances for all companies in parallel
          const instancesMap: Record<string, ProductInstance[]> = {};
          await Promise.all(
            data.map(async (company) => {
              try {
                const instances = await getProductInstancesForCompany(company.id);
                instancesMap[company.id] = instances.map((instance) => ({
                  ...instance,
                  apiKey: null as string | null,
                }));
              } catch (error) {
                console.error(`Error loading product instances for company ${company.id}:`, error);
                instancesMap[company.id] = [];
              }
            })
          );
          setCompanyProductInstances(instancesMap);
        }
      } catch (error) {
        console.error('Error loading companies:', error);
        toast.error('Failed to load companies');
      } finally {
        setIsLoading(false);
      }
    };

  // Helper to check if a company is the DEFAULT company (cannot be edited)
  const isDefaultCompany = (companyCode: string): boolean => {
    return companyCode === 'DEFAULT';
  };

  const handleCompanyClick = (companyId: string) => {
    // Check if this is the DEFAULT company
    const company = companies.find((c) => c.id === companyId);
    if (company && isDefaultCompany(company.code)) {
      return; // Do not allow viewing/editing DEFAULT company
    }
    // Open integration configuration dialog instead of navigating
    handleConfigureIntegrations(companyId);
  };

  const handleConfigureIntegrations = async (companyId: string) => {
    const companyListItem = companies.find((c) => c.id === companyId);
    if (companyListItem && isDefaultCompany(companyListItem.code)) {
      return; // Do not allow configuring integrations for DEFAULT company
    }
    if (companyListItem) {
      try {
        // Load product instances from mock or API based on mode
        const instances = isDemo 
          ? getMockProductInstancesForCompany(companyId)
          : await getProductInstancesForCompany(companyId);
        
        // Map instances to Company's ProductInstance type (add missing apiKey)
        const productInstances = instances.map((instance) => ({
          ...instance,
          apiKey: null as string | null,
        }));
        
        const fullCompany: Company = {
          id: companyListItem.id,
          name: companyListItem.name,
          code: companyListItem.code,
          status: companyListItem.status,
          description: '',
          activeProjects: companyListItem.activeProjects,
          employeeCount: companyListItem.employeeCount,
          productInstances: productInstances,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        setSelectedCompany(fullCompany);
        setIsIntegrationDialogOpen(true);
      } catch (error) {
        console.error('Error loading product instances:', error);
        toast.error('Failed to load integration configuration');
      }
    }
  };

  const handleCompanyEdit = async (companyId: string) => {
    try {
      const company = companies.find((c) => c.id === companyId);
      if (company && isDefaultCompany(company.code)) {
        return; // Do not allow editing DEFAULT company
      }
      if (company) {
        // Load product instances from mock or API based on mode
        const instances = isDemo 
          ? getMockProductInstancesForCompany(companyId)
          : await getProductInstancesForCompany(companyId);
        const productInstances = instances.map((instance) => ({
          ...instance,
          apiKey: null as string | null,
        }));
        
        setEditingCompany({
          ...company,
          productInstances,
          financialIntegration: null,
          hrIntegration: null,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Company);
        setIsFormOpen(true);
      }
    } catch (error) {
      console.error('Error loading company for edit:', error);
      toast.error('Failed to load company details');
    }
  };

  const handleCompanyCreate = () => {
    setEditingCompany(null);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: CreateCompanyRequest | UpdateCompanyRequest) => {
    try {
      if (editingCompany) {
        // Update existing company
        if (isDemo) {
          await mockCompanyService.updateCompany(editingCompany.id, data as UpdateCompanyRequest);
        } else {
          await companyService.updateCompany(editingCompany.id, data as UpdateCompanyRequest);
        }
        toast.success('Company updated successfully');
      } else {
        // Create new company - ensure status has a default value
        const createData = data as CreateCompanyRequest;
        if (isDemo) {
          await mockCompanyService.createCompany({
            name: createData.name,
            code: createData.code,
            description: createData.description,
            status: createData.status || 'setup',
          });
        } else {
          await companyService.createCompany({
            name: createData.name,
            code: createData.code,
            description: createData.description,
            status: createData.status || 'setup',
          });
        }
        toast.success('Company created successfully');
      }
      
      // Reload companies list on page and in header
      await loadCompanies();
      await refreshHeaderCompanies();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error saving company:', error);
      throw error; // Re-throw to let the form handle the error
    }
  };

  const handleValidateCode= async (code: string, excludeId?: string): Promise<boolean> => {
    try {
      if (isDemo) {
        return await mockCompanyService.validateCompanyCode(code, excludeId);
      }
      return await companyService.validateCompanyCode(code, excludeId);
    } catch (error) {
      console.error('Error validating company code:', error);
      return false;
    }
  };

  // API callbacks for Live mode integration management
  const handleAddProduct = async (companyId: string, request: AddProductRequest): Promise<ProductInstance[]> => {
    try {
      const dto = await createProductInstance(companyId, request);
      const instance = mapProductInstanceDtoToProductInstance(dto);
      // Add apiKey property to match Company's ProductInstance type
      const productInstance: ProductInstance = {
        ...instance,
        apiKey: null,
      };
      toast.success('Product added successfully');
      return [productInstance];
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error('Failed to add product');
      throw error;
    }
  };

  const handleUpdateProduct = async (companyId: string, instanceId: string, request: UpdateProductRequest): Promise<ProductInstance> => {
    try {
      const dto = await updateProductInstance(companyId, instanceId, request);
      const instance = mapProductInstanceDtoToProductInstance(dto);
      // Add apiKey property to match Company's ProductInstance type
      const productInstance: ProductInstance = {
        ...instance,
        apiKey: null,
      };
      toast.success('Product updated successfully');
      return productInstance;
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product');
      throw error;
    }
  };

  const handleDeleteProduct = async (companyId: string, instanceId: string): Promise<void> => {
    try {
      await deleteProductInstance(companyId, instanceId);
      toast.success('Product deleted successfully');
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
      throw error;
    }
  };

  const handleRefreshProducts = async (companyId: string): Promise<ProductInstance[]> => {
    try {
      const instances = await getProductInstancesForCompany(companyId);
      // Add apiKey property to match Company's ProductInstance type
      return instances.map((instance) => ({
        ...instance,
        apiKey: null as string | null,
      }));
    } catch (error) {
      console.error('Error refreshing products:', error);
      toast.error('Failed to refresh products');
      throw error;
    }
  };

  const handleSyncProduct = async (companyId: string, instanceId: string) => {
    try {
      const result = await syncProductInstance(companyId, instanceId);
      const totalRecords = result.recordsImported + result.recordsUpdated;
      if (result.status === 'failed') {
        toast.error(`Sync failed: ${result.errors?.join(', ') || 'Unknown error'}`);
      } else if (result.recordsFailed > 0) {
        toast.warning(`Sync completed with ${result.recordsFailed} failures. ${totalRecords} records processed.`);
      } else {
        toast.success(`Sync completed successfully. ${totalRecords} records processed.`);
      }
      return {
        productInstanceId: result.productInstanceId,
        status: result.status as 'success' | 'partial' | 'failed',
        recordsImported: result.recordsImported,
        recordsUpdated: result.recordsUpdated,
        recordsFailed: result.recordsFailed,
        startedAt: result.startedAt,
        completedAt: result.completedAt,
        errors: result.errors,
        warnings: result.warnings,
      };
    } catch (error) {
      console.error('Error syncing product:', error);
      toast.error('Failed to sync product');
      throw error;
    }
  };

  const handleSaveIntegration = async (updatedCompany: Company) => {
    // In Live mode, changes are already persisted via the individual API callbacks
    // In Demo mode, we just show a success message
    toast.success('Integration configuration saved successfully');
    // Reload companies list on page and in header
    await loadCompanies();
    await refreshHeaderCompanies();
  };

  return (
    <div className="space-y-6">
      <CompanyList
              companies={companies}
              companyProductInstances={companyProductInstances}
              isLoading={isLoading}
              onCompanyClick={handleCompanyClick}
              onCompanyEdit={handleCompanyEdit}
              onCompanyCreate={handleCompanyCreate}
              onConfigureIntegrations={handleConfigureIntegrations}
              onRefresh={loadCompanies}
            />

      <CompanyFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        company={editingCompany}
        onSubmit={handleFormSubmit}
        onValidateCode={handleValidateCode}
      />

      <IntegrationConfigDialog
        open={isIntegrationDialogOpen}
        onOpenChange={setIsIntegrationDialogOpen}
        company={selectedCompany}
        onSave={handleSaveIntegration}
        onAddProduct={isLive ? handleAddProduct : undefined}
        onUpdateProduct={isLive ? handleUpdateProduct : undefined}
        onDeleteProduct={isLive ? handleDeleteProduct : undefined}
        onSyncProduct={isLive ? handleSyncProduct : undefined}
        onRefresh={isLive ? handleRefreshProducts : undefined}
        isLiveMode={isLive}
      />
    </div>
  );
}
